import type * as Sentry from '@sentry/react'
import { isNetworkError } from 'utils/js'
import { sanitizePaymentTelemetry } from 'utils/payment-return-url'

import {
  SentryFilterTagOneClickUpgradeButton,
  SentryFilterTagOneClickUpgradeToggle,
  SentryFilterTagOneClickYearlyExtension,
  SentryFilterTagPaymentsDialog,
  SentryFilterTagPaymentsButton,
  SentryFilterTagAutostartFreeTrial,
  SentryFilterCoinsTopUp,
} from './constants'

const sentryPaymentTags = new Set([
  SentryFilterTagPaymentsButton,
  SentryFilterTagPaymentsDialog,
  SentryFilterTagOneClickUpgradeButton,
  SentryFilterTagOneClickUpgradeToggle,
  SentryFilterTagOneClickYearlyExtension,
  SentryFilterTagAutostartFreeTrial,
  SentryFilterCoinsTopUp,
])

const networkErrorEventTypes = new Set(['TypeError', 'ConnectError'])

const abortErrorMessages = ['The user aborted a request.', 'signal is aborted without reason']

function isAbortError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }

  if (error.name === 'AbortError') {
    return true
  }

  if (error instanceof DOMException && error.code === DOMException.ABORT_ERR) {
    return true
  }

  return abortErrorMessages.some(msg => error.message.includes(msg))
}

function isAbortErrorEvent(ex: Sentry.Exception): boolean {
  if (ex.type === 'AbortError' || ex.type === 'DOMException') {
    return abortErrorMessages.some(msg => ex.value?.includes(msg) ?? false)
  }

  if (ex.type === 'Error') {
    return abortErrorMessages.some(msg => ex.value?.includes(msg) ?? false)
  }

  return false
}

export function beforeSend(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null {
  // Browser network errors (`TypeError: Failed to fetch` and friends) only
  // fire when the request never completes — virtually always client-side
  // noise (network drop, ad blocker, CORS, DNS, SSL, backgrounded tab).
  // Server-side failures arrive as HTTP responses, not fetch `TypeError`s.
  //
  // Two checks:
  // 1. hint.originalException — the error object passed to captureException.
  // 2. event.exception.values — the processed exception chain in the Sentry
  //    event. Catches ConnectError wrapping, cross-realm errors (chrome
  //    extensions), and other cases where hint.originalException is unreliable.
  //    Uses `every` so a chain containing both a network error and a real app
  //    error is retained. Type-gated to TypeError/ConnectError to avoid
  //    dropping unrelated errors whose message happens to contain a fragment.
  if (isNetworkError(hint?.originalException)) {
    return null
  }

  // AbortError / DOMException(ABORT_ERR) — user navigated away, cancelled an
  // AI generation, closed a tab, or a component unmounted while a fetch was
  // in-flight. These are expected user-initiated cancellations, not bugs.
  // Covers STUDIO-MP (3.8 k events, 882 users).
  if (isAbortError(hint?.originalException)) {
    return null
  }

  const values = event.exception?.values
  if (
    values &&
    values.length > 0 &&
    values.every(ex => networkErrorEventTypes.has(ex.type ?? '') && isNetworkError(ex.value))
  ) {
    return null
  }

  if (values && values.length > 0 && values.every(isAbortErrorEvent)) {
    return null
  }

  Object.assign(event, sanitizePaymentTelemetry(event))
  return event
}

export function beforeSendPayments(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
): Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null {
  const nextEvent = beforeSend(event, hint)
  if (!nextEvent) {
    return null
  }

  if (event.tags?.component && sentryPaymentTags.has(event.tags.component as string)) {
    return event
  }

  return null
}
